import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from "react-redux";
import {useQuery} from "@tanstack/react-query";
import {useNavigate} from "react-router-dom";

import styles from './AddPhoto.module.scss';
import trashIcon from '../../constants/images/trash-icon.svg';
import {stepperInfoActions} from "../../store/slices/stepperInfoSlice";
import {modalPreviewSelectedPhotoActions} from "../../store/slices/modalPreviewSelectedPhotoSlice";
import ModalPreviewSelectedPhoto from "../ModalPreviewSelectedPhoto/ModalPreviewSelectedPhoto";
import {qrCodeService} from "../../services/qrCode.service";
import {resolveMediaUrl} from "../../configs/urls";
import {qrCodeActions} from "../../store/slices/qrCodeSlice";
import {UIActions} from "../../store/slices/UISlice";
import {MAIN_ROUTES} from "../../routing/main_routes";
import toast from "react-hot-toast";
import Compressor from "compressorjs";
import {convertHeicList} from "../../utils/heicConvert";

// A single QR-synced photo tile. It loads the image off-screen and only paints
// the thumbnail once the bytes have actually arrived, showing a spinner in the
// meantime and an error mark if the media can't be fetched. Reports its final
// state up so the parent can block "next/submit" until every photo resolves.
const QrPhotoTile = ({src, onClick, onResolved}) => {
  const [status, setStatus] = useState('loading'); // 'loading' | 'loaded' | 'error'

  return (
    <div
      className={styles.add}
      onClick={status === 'loaded' ? onClick : undefined}
      style={status === 'loaded' ? {backgroundImage: `url(${src})`} : undefined}
    >
      <img
        src={src}
        alt=""
        style={{display: 'none'}}
        onLoad={() => {
          setStatus('loaded');
          onResolved(src, 'loaded');
        }}
        onError={() => {
          setStatus('error');
          onResolved(src, 'error');
        }}
      />
      {status === 'loading' && <span className={styles.tileSpinner} aria-label="Завантаження фото"/>}
      {status === 'error' && <span className={styles.tileError}>!</span>}
    </div>
  );
};

const AddPhoto = ({setCurrentStep}) => {
  const [photo, setPhoto] = useState(null);

  const navigate = useNavigate();

  const dispatch = useDispatch();
  const {selectedFiles, request_files} = useSelector(state => state.stepperInfo);
  const {isPreviewOpen} = useSelector(state => state.modalPreviewSelectedPhoto);
  const {session_id, photos, qr_code} = useSelector(state => state.qrCode);
  const {error} = useSelector(state => state.UI);

  // Guard so a QR session is created at most once per mount. Without it the
  // generate() POST re-fires from the mount effect, the session timeout and the
  // 404 throwOnError handler (each of which mutates session_id/photos/qr_code in
  // the store), producing repeated /generate-qr/ requests with new session_ids
  // and flickering the photo placeholder card on every reset.
  const generatingRef = useRef(false);

  const generateQrCode = async () => {
    if (generatingRef.current) return;
    generatingRef.current = true;
    try {
      await dispatch(qrCodeActions.generate());
    } catch (error) {
      generatingRef.current = false;
      console.error(error);
    }
  }

  useEffect(() => {
    if (!session_id) {
      generateQrCode();
    }

    const timeout = setTimeout(async () => {
      setCurrentStep(1);
      localStorage.setItem('currentStep', JSON.stringify(1));
      dispatch(qrCodeActions.clearAllData());
      dispatch(stepperInfoActions.clearAllData());
      dispatch(UIActions.setIsSessionEnded(true));

      if (!session_id) {
        await generateQrCode();
      }
      navigate(MAIN_ROUTES.CREATE_ORDER_STEP_1);
    }, 58.5 * 60 * 1000);

    return () => clearInterval(timeout);
  }, []);

  const {data} = useQuery({
    // session_id is part of the key so the poll (re)starts as soon as the QR
    // session is generated; ``enabled`` keeps the query from running with a
    // null session_id (which would otherwise return undefined and error).
    queryKey: ['get-photos-qr-code', session_id],
    queryFn: () => qrCodeService.getPhotos(session_id),
    enabled: !!session_id,
    throwOnError: async (error) => {
      if (error?.response?.status === 404) {
        dispatch(qrCodeActions.clearAllData());
        setCurrentStep(1);
        localStorage.setItem('currentStep', JSON.stringify(1));
        dispatch(stepperInfoActions.clearAllData());
        await generateQrCode();
        alert('Сесія завантаження фото за допомогою QR коду закінчилась! Спробуйте ще раз!');
      }
    },
    // Poll continuously while this step is mounted so EVERY uploaded photo
    // syncs. The old behaviour stopped after the first photo arrived, which
    // dropped any photos the phone sent in a later batch / after the first poll
    // (the "7 uploaded, 5 shown" bug). The poll stops when the user leaves the
    // step (unmount) or the session times out.
    refetchInterval: 3000,
    refetchIntervalInBackground: true
  });

  useEffect(() => {
    // Never overwrite the store with undefined: that would make the
    // refetchInterval condition above evaluate false and stop the poll before
    // any uploaded photo is fetched.
    if (data?.photos) {
      dispatch(qrCodeActions.setPhotos(data.photos));
    }
  }, [data, dispatch])

  // Per-URL load result: undefined = still in flight, 'loaded' / 'error' = done.
  const [resolvedPhotos, setResolvedPhotos] = useState({});

  const handlePhotoResolved = useCallback((src, result) => {
    setResolvedPhotos(prev => (prev[src] === result ? prev : {...prev, [src]: result}));
  }, []);

  // Block "next/submit" while any QR photo is still arriving or not yet loaded
  // in the browser. An errored photo counts as "resolved" (it'll be skipped on
  // upload) so a single broken media URL can never freeze the funnel forever.
  useEffect(() => {
    const urls = (photos || []).map(resolveMediaUrl);
    const syncing = urls.length > 0 && urls.some(url => resolvedPhotos[url] === undefined);
    dispatch(qrCodeActions.setPhotosSyncing(syncing));
  }, [photos, resolvedPhotos, dispatch]);

  // Leaving the step must never leave the gate stuck on.
  useEffect(() => () => {
    dispatch(qrCodeActions.setPhotosSyncing(false));
  }, [dispatch]);

  const handleAddPhoto = () => {
    document.getElementById('fileInput').click();
  };

  const handleGetPhotosFromInput = async (e) => {
    // iPhone HEIC -> JPEG first (canvas compressors + backend can't read HEIC).
    const selectedInput = await convertHeicList(e.target.files);
    if (selectedInput.length === 0) return;

    const allowedTypes = ["image/png", "image/jpeg", "image/jpg"];

    if (selectedInput.some(file => !allowedTypes.includes(file.type))) {
      toast.error("Дозволені тільки файли у форматах PNG, JPEG та JPG", {id: "allow-format", duration: 5000});
      return;
    }

    // Compress best-effort while preserving selection order. Promise.all returns
    // the resolved values in input order, so compressedFiles stays index-aligned
    // with the preview urls below (delete-by-index relies on that). A compression
    // failure must NOT reject - that would reject the whole batch and silently
    // drop every selected photo - so on error we fall back to the original file.
    const compressedFiles = await Promise.all(selectedInput.map((file) =>
      new Promise((resolve) => {
        new Compressor(file, {
          quality: 0.6,
          success: (compressedResult) => {
            resolve(new File([compressedResult], file.name, {type: compressedResult.type}));
          },
          error: (err) => {
            console.error('Compression failed:', err);
            resolve(file);
          },
        });
      })
    ));

    const totalSize = [...request_files, ...compressedFiles].reduce((acc, file) => acc + file.size, 0);

    const maxSizeForDb = 20 * 1024 * 1024; // 20 MB

    if (totalSize >= maxSizeForDb) {
      toast.error("Максимальний розмір файлів - 20МБ", {duration: 5000, id: "max-size"});
      return;
    }

    const urls = selectedInput.map(file => ({
      name: file.name,
      size: file.size,
      url: URL.createObjectURL(file),
    }));

    dispatch(stepperInfoActions.setRequestFiles([...request_files, ...compressedFiles]));
    dispatch(stepperInfoActions.setSelectedFiles([...selectedFiles, ...urls]));
  };

  useEffect(() => {
    return () => {
      toast.remove("max-size");
      toast.remove("allow-format");
    }
  }, []);

  const handleDeleteSelectedPhoto = (event, index) => {
    // don`t overlap on parent div (opening modal window)
    event.stopPropagation();

    const updatedPhotos = [...selectedFiles];
    const updatedFiles = [...request_files];
    updatedPhotos.splice(index, 1);
    updatedFiles.splice(index, 1);
    dispatch(stepperInfoActions.setSelectedFiles(updatedPhotos));
    dispatch(stepperInfoActions.setRequestFiles(updatedFiles));
  };

  useEffect(() => {
    if (request_files?.length > 0) {
      if (!(request_files[0] instanceof File)) {
        dispatch(stepperInfoActions.setSelectedFiles([]));
        dispatch(stepperInfoActions.setRequestFiles([]));
      }
    }
  }, []);

  const handleSelectPhotoForPreview = (file) => {
    if (data?.photos?.length > 0) {
      setPhoto(resolveMediaUrl(file));
    } else {
      setPhoto(file.url);
    }
    dispatch(modalPreviewSelectedPhotoActions.openPreview('AddPhoto'));
  };

  return (
    <div className={styles.addPhotoWrapper}>
      <div className={styles.addPhotoWrapper_description}>
        <p className={styles.addPhotoWrapper_description_title}>Додати фото забруднень</p>
        <p className={styles.addPhotoWrapper_description_subtitle}></p>
      </div>

      <div className={styles.addPhotoWrapper_addPhotoContainer}>
        <div className={styles.addPhotoWrapper_addPhotoContainer_buttons}>
          <button
            style={{display: photos?.length > 0 ? 'none' : 'flex'}}
            onClick={handleAddPhoto}
            className={styles.addFullWidth}
          >
            <span>Додати фото</span>
            <span>Формат: PNG, JPG, JPEG (Макс. 20мб)</span>
          </button>
          <input
            onChange={handleGetPhotosFromInput}
            multiple
            type="file"
            id="fileInput"
            accept={'image/*'}
            style={{display: 'none'}}
          />

          <div className={styles.addPhotoWrapper_addPhotoContainer_buttons_add}>
            {selectedFiles?.map((file, index) =>
              <div
                key={index}
                className={styles.add}
                onClick={() => handleSelectPhotoForPreview(file)}
                style={{backgroundImage: `url(${file?.url})`}}
              >
                <div onClick={(event) => handleDeleteSelectedPhoto(event, index)}>
                  <img src={trashIcon} alt="trash-icon"/>
                </div>
              </div>
            )}

            {(selectedFiles?.length === 0 && selectedFiles.length < 3 && photos?.length === 0) ?
              (Array.from({length: 3 - selectedFiles?.length})?.map((_, index) => (
                  // Array.from({length: 3 - (type === 'create' ? selectedFiles?.length : orderForEdit?.selectedFiles?.length)})?.map((_, index) => (
                  <button
                    key={index}
                    onClick={handleAddPhoto}
                    className={styles.add}
                  >+</button>
                ))
              ) : null}
            {photos?.length > 0 && photos.map((file) => {
              const src = resolveMediaUrl(file);
              return (
                <QrPhotoTile
                  key={src}
                  src={src}
                  onClick={() => handleSelectPhotoForPreview(file)}
                  onResolved={handlePhotoResolved}
                />
              );
            })}
          </div>
        </div>

        {isPreviewOpen &&
          <ModalPreviewSelectedPhoto
            photo={photo}
            component={'AddPhoto'}
          />}

        <div
          className={`${styles.addPhotoWrapper_addPhotoContainer_qr} 
                    ${(selectedFiles?.length > 0 || data?.photos?.length > 0) ? styles.hide : ''}`}
        >
          <img src={`data:image/png;base64,${qr_code}`} alt="qr code"/>
        </div>
      </div>
    </div>
  );
};

export default AddPhoto;
